import {
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { LuCalendarOff, LuCircleCheck, LuCircleX } from 'react-icons/lu'
import { CustomerAvatar } from '@/frontend/components/marina/CustomerAvatar'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { useOccupancyAbsences } from '@/frontend/hooks/useOccupancy'

type AbsenceTab = 'availabilities' | 'terms'

export const Route = createFileRoute('/dashboard/occupancy/absences')({
  validateSearch: (s: Record<string, unknown>): { tab?: AbsenceTab } => ({
    tab: s.tab === 'terms' ? 'terms' : undefined,
  }),
  component: AbsencesPage,
})

function AbsencesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'availabilities'
  const setTab = (v: AbsenceTab) => navigate({ search: () => ({ tab: v }), replace: true })

  // Fetch both counts for badges
  const { data: availData, isLoading: availLoading } = useOccupancyAbsences({ status: 'AVAILABLE' })
  const { data: blockedData, isLoading: blockedLoading } = useOccupancyAbsences({ status: 'BLOCKED' })

  const isLoading = tab === 'availabilities' ? availLoading : blockedLoading
  const items = tab === 'availabilities' ? (availData?.items ?? []) : (blockedData?.items ?? [])

  const availCount = availData?.items.length ?? 0
  const blockedCount = blockedData?.items.length ?? 0

  // Stats
  const avgDuration =
    items.length > 0
      ? Math.round(items.reduce((s, a) => s + dayjs(a.returnDate).diff(dayjs(a.departure), 'day'), 0) / items.length)
      : 0
  const uniqueBerths = new Set(items.map((a) => a.berthId)).size

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group gap="sm" align="center">
          <ThemeIcon variant="light" color="laut" size="lg" radius="md">
            <LuCalendarOff size={18} />
          </ThemeIcon>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              {t('page.occupancy.absences.title')}
            </Title>
            <Text size="xs" c="dimmed">
              {t('page.occupancy.absences.subtitle')}
            </Text>
          </div>
        </Group>

        {/* Stats strip */}
        {!isLoading && items.length > 0 && (
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuCalendarOff size={13} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.absences.stat_total')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {items.length}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.absences.stat_avg')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {avgDuration} hari
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {t('page.occupancy.absences.stat_berths')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {uniqueBerths}
                </Text>
              </Group>
            </Card>
          </SimpleGrid>
        )}

        {/* Pill Nav with counts */}
        <PillNav
          value={tab}
          onChange={setTab}
          items={[
            {
              value: 'availabilities',
              label: t('page.occupancy.absences.tab_available'),
              icon: <LuCircleCheck size={13} />,
              count: availCount,
              countColor: 'teal',
            },
            {
              value: 'terms',
              label: t('page.occupancy.absences.tab_blocked'),
              icon: <LuCircleX size={13} />,
              count: blockedCount,
              countColor: 'red',
            },
          ]}
        />

        {/* Context hint per tab */}
        <Text size="xs" c="dimmed">
          {tab === 'availabilities'
            ? t('page.occupancy.absences.hint_available')
            : t('page.occupancy.absences.hint_blocked')}
        </Text>

        {/* Table */}
        {isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton count
              <Skeleton key={i} height={56} radius="md" />
            ))}
          </Stack>
        ) : (
          <Card radius="md" padding="lg">
            <DataTable
              isEmpty={items.length === 0}
              emptyLabel={
                tab === 'availabilities'
                  ? t('page.occupancy.absences.empty_available')
                  : t('page.occupancy.absences.empty_blocked')
              }
              page={1}
              pageSize={50}
              total={items.length}
              columns={
                <>
                  <Table.Th>{t('page.occupancy.absences.col_customer')}</Table.Th>
                  <Table.Th>{t('page.occupancy.absences.col_berth')}</Table.Th>
                  <Table.Th>{t('page.occupancy.absences.col_period')}</Table.Th>
                  <Table.Th ta="center">{t('page.occupancy.absences.col_duration')}</Table.Th>
                  <Table.Th>{t('page.occupancy.absences.col_reason')}</Table.Th>
                  <Table.Th>{t('page.occupancy.absences.col_status')}</Table.Th>
                  <Table.Th ta="right">{t('page.occupancy.absences.col_booking')}</Table.Th>
                </>
              }
            >
              {items.map((a) => {
                const customer = a.booking?.customer
                const name = customer ? `${customer.firstName} ${customer.lastName}`.trim() : '—'
                const duration = dayjs(a.returnDate).diff(dayjs(a.departure), 'day')
                const isActive = dayjs().isAfter(dayjs(a.departure)) && dayjs().isBefore(dayjs(a.returnDate))
                const isPast = dayjs().isAfter(dayjs(a.returnDate))

                return (
                  <Table.Tr key={a.id}>
                    {/* Customer */}
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <CustomerAvatar name={name} size={30} />
                        <Stack gap={1}>
                          <Text size="sm" fw={600} truncate style={{ maxWidth: 140 }}>
                            {name}
                          </Text>
                          {customer?.category && <KindBadge category={customer.category} />}
                        </Stack>
                      </Group>
                    </Table.Td>

                    {/* Berth */}
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {a.berth?.code ?? '—'}
                      </Text>
                    </Table.Td>

                    {/* Period */}
                    <Table.Td>
                      <Text size="sm">
                        {dayjs(a.departure).format('DD MMM')} → {dayjs(a.returnDate).format('DD MMM YYYY')}
                      </Text>
                      {isActive && (
                        <Badge size="xs" color="teal" variant="light" mt={2}>
                          Sedang berlangsung
                        </Badge>
                      )}
                      {isPast && (
                        <Badge size="xs" color="gray" variant="light" mt={2}>
                          Selesai
                        </Badge>
                      )}
                    </Table.Td>

                    {/* Duration */}
                    <Table.Td ta="center">
                      <Badge variant="light" color={duration > 30 ? 'orange' : 'blue'} size="sm" radius="xl">
                        {duration}h
                      </Badge>
                    </Table.Td>

                    {/* Reason */}
                    <Table.Td>
                      {a.reason ? (
                        <Tooltip label={a.reason} withArrow multiline w={200}>
                          <Text size="sm" truncate style={{ maxWidth: 160, cursor: 'default' }}>
                            {a.reason}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>

                    {/* Status */}
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={a.status === 'AVAILABLE' ? 'teal' : 'red'}
                        variant="light"
                        leftSection={a.status === 'AVAILABLE' ? <LuCircleCheck size={10} /> : <LuCircleX size={10} />}
                      >
                        {a.status === 'AVAILABLE' ? 'Tersedia' : 'Diblokir'}
                      </Badge>
                    </Table.Td>

                    {/* Booking link */}
                    <Table.Td ta="right">
                      <Anchor
                        component={Link}
                        to={`/dashboard/reservations/${a.booking?.id}` as any}
                        c="laut"
                        size="sm"
                        fw={500}
                      >
                        {a.booking?.code ?? '—'} →
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </DataTable>
          </Card>
        )}
      </Stack>
    </Container>
  )
}
